// src/pages/auth/ForgotPasswordPage.tsx - Enhanced with error handling
import React, { useState } from 'react';
import { Mail, ArrowLeft, CheckCircle, AlertCircle, Loader } from 'lucide-react';

interface ForgotPasswordPageProps {
  onNavigateToLogin?: () => void;
  onSendResetEmail?: (email: string) => Promise<void>;
  onSubmit: (email: string) => Promise<void>;
  embedded?: boolean;
}

export const ForgotPasswordPage: React.FC<ForgotPasswordPageProps> = ({
  onNavigateToLogin,
  onSendResetEmail,
  onSubmit,
  embedded = false
}) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (onSubmit) {
        await onSubmit(email);
      } else if (onSendResetEmail) {
        await onSendResetEmail(email);
      } else {
        await new Promise(resolve => setTimeout(resolve, 2000));
        // Password reset email sent (mock mode)
      }
      
      setEmailSent(true);
    } catch (error: any) {
      const errorMessage = error?.message || String(error);
      const errorCode = error?.code || '';
      
      let displayError = 'Failed to send reset email. Please try again.';
      
      if (errorCode === 'USER_NOT_FOUND' || errorMessage.toLowerCase().includes('no account found')) {
        displayError = 'No account found with this email address.';
      } else if (errorCode === 'RATE_LIMITED' || errorMessage.toLowerCase().includes('too many')) {
        displayError = 'Too many reset attempts. Please wait a few minutes and try again.';
      } else if (errorMessage) {
        displayError = errorMessage;
      }
      
      setError(displayError);
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div style={{
        minHeight: embedded ? 'auto' : '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: embedded ? 'transparent' : 'var(--color-background)',
        padding: embedded ? '0' : 'var(--space-6)',
        fontFamily: 'var(--font-primary)'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '400px',
          textAlign: 'center',
          backgroundColor: 'var(--color-background)',
          padding: 'var(--space-8)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--color-border)'
        }}>
          <CheckCircle size={64} color="var(--color-success)" style={{ margin: '0 auto var(--space-6)' }} />
          
          <h1 style={{
            fontFamily: 'var(--font-primary)',
            fontSize: 'var(--text-2xl)',
            fontWeight: 'var(--font-bold)',
            color: 'var(--color-primary)',
            margin: '0 0 var(--space-4) 0',
            letterSpacing: 'var(--tracking-tight)'
          }}>
            Check Your Email
          </h1>

          <p style={{
            fontFamily: 'var(--font-primary)',
            fontSize: 'var(--text-base)',
            color: 'var(--color-secondary)',
            margin: '0 0 var(--space-2) 0',
            lineHeight: 'var(--leading-relaxed)'
          }}>
            If an account with <strong style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-primary)' }}>{email}</strong> exists, you will receive a password reset link shortly.
          </p>

          <p style={{
            fontFamily: 'var(--font-primary)',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-muted)',
            margin: '0 0 var(--space-6) 0',
            lineHeight: 'var(--leading-relaxed)'
          }}>
            Please check your spam folder if you don't see the email in your inbox.
          </p>

          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)'
          }}>
            <button
              onClick={onNavigateToLogin}
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--color-background)',
                backgroundColor: 'var(--color-primary)',
                border: 'none',
                borderRadius: 'var(--radius)',
                cursor: 'pointer',
                transition: 'var(--transition-fast)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-primary)'}
            >
              <ArrowLeft size={16} />
              Back to Login
            </button>

            <button
              onClick={() => {
                setEmailSent(false);
                setEmail('');
                setError(null);
              }}
              style={{
                padding: 'var(--space-2)',
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-secondary)',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                transition: 'var(--transition-fast)',
                textDecoration: 'underline'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-primary)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-secondary)'}
            >
              Try a different email
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: embedded ? 'auto' : '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: embedded ? 'transparent' : 'var(--color-background)',
      padding: embedded ? '0' : 'var(--space-6)',
      fontFamily: 'var(--font-primary)'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        backgroundColor: 'var(--color-background)',
        padding: 'var(--space-8)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-lg)',
        border: '1px solid var(--color-border)'
      }}>
        <button
          onClick={onNavigateToLogin}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-2)',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'var(--font-primary)',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-secondary)',
            marginBottom: 'var(--space-6)',
            transition: 'var(--transition-fast)'
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-primary)'}
          onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-secondary)'}
        >
          <ArrowLeft size={16} />
          Back to Login
        </button>

        <div style={{ 
          textAlign: 'center', 
          marginBottom: 'var(--space-8)' 
        }}>
          <h1 style={{
            fontFamily: 'var(--font-primary)',
            fontSize: 'var(--text-3xl)',
            fontWeight: 'var(--font-bold)',
            color: 'var(--color-primary)',
            margin: '0 0 var(--space-2) 0',
            letterSpacing: 'var(--tracking-tight)'
          }}>
            Reset Password
          </h1>
          <p style={{
            fontFamily: 'var(--font-primary)',
            fontSize: 'var(--text-base)',
            color: 'var(--color-secondary)',
            margin: 0,
            lineHeight: 'var(--leading-relaxed)'
          }}>
            Enter your email address and we'll send you a link to reset your password
          </p>
        </div>

        {error && (
          <div style={{
            padding: 'var(--space-3)',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 'var(--radius)',
            marginBottom: 'var(--space-6)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>
            <AlertCircle size={16} color="var(--color-error)" />
            <span style={{
              fontFamily: 'var(--font-primary)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-error)',
              fontWeight: 'var(--font-medium)'
            }}>
              {error}
            </span>
          </div>
        )}

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-6)'
        }}>
          <div>
            <label style={{
              display: 'block',
              fontFamily: 'var(--font-primary)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)',
              color: 'var(--color-primary)',
              marginBottom: 'var(--space-2)'
            }}>
              Email Address
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
                placeholder="Enter your email"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: 'var(--space-3) var(--space-3) var(--space-3) var(--space-10)',
                  border: `1px solid ${error ? 'var(--color-error)' : 'var(--color-border)'}`,
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--font-primary)',
                  fontSize: 'var(--text-base)',
                  color: 'var(--color-primary)',
                  backgroundColor: isLoading ? 'var(--color-surface)' : 'var(--color-background)',
                  outline: 'none',
                  transition: 'var(--transition-fast)',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  if (!error) {
                    e.currentTarget.style.borderColor = 'var(--color-primary)';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 0, 0, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = error ? 'var(--color-error)' : 'var(--color-border)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              <Mail 
                size={16} 
                color="var(--color-muted)" 
                style={{
                  position: 'absolute',
                  left: 'var(--space-3)',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading || !email}
            style={{
              width: '100%',
              padding: 'var(--space-4)',
              fontFamily: 'var(--font-primary)',
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-medium)',
              color: (isLoading || !email) ? 'var(--color-muted)' : 'var(--color-background)',
              backgroundColor: (isLoading || !email) ? 'var(--color-surface)' : 'var(--color-primary)',
              border: 'none',
              borderRadius: 'var(--radius)',
              cursor: (isLoading || !email) ? 'not-allowed' : 'pointer',
              transition: 'var(--transition-fast)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-2)'
            }}
            onMouseEnter={(e) => {
              if (!isLoading && email) {
                e.currentTarget.style.backgroundColor = '#1f2937';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading && email) {
                e.currentTarget.style.backgroundColor = 'var(--color-primary)';
              }
            }}
          >
            {isLoading && (
              <Loader size={16} className="animate-spin" />
            )}
            {isLoading ? 'Sending...' : 'Send Reset Link'}
          </button>

          <div style={{
            textAlign: 'center',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--color-surface)',
            borderRadius: 'var(--radius)',
            border: '1px solid var(--color-border)'
          }}>
            <p style={{
              fontFamily: 'var(--font-primary)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-secondary)',
              margin: '0 0 var(--space-2) 0',
              fontWeight: 'var(--font-medium)'
            }}>
              💡 Remember your password?
            </p>
            <button
              onClick={onNavigateToLogin}
              style={{
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-primary)',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 'var(--font-medium)',
                textDecoration: 'underline',
                transition: 'var(--transition-fast)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'none'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'underline'}
            >
              Sign in instead
            </button>
          </div>
        </div>

        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            
            .animate-spin {
              animation: spin 1s linear infinite;
            }
          `}
        </style>
      </div>
    </div>
  );
};